// src/components/Home.js
import React, { useEffect, useState } from 'react';
import { getLeaderboard, getCircuits, getUserLaps } from '../utils/firestoreUtils';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Container, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';

const formatLapTime = (time) => {
  if (!time) return '';
  const s = parseFloat(time);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s % 1) * 1000);
  if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

// Lap time — clickable if proof URL exists
function LapTimeLink({ time, proof, style = {} }) {
  const formatted = formatLapTime(time);
  if (!formatted) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const base = { fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600, ...style };
  if (proof) {
    return (
      <a href={proof} target="_blank" rel="noopener noreferrer"
        style={{ ...base, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}
        title="View proof">
        {formatted}<span style={{ fontSize: '0.7em', opacity: 0.7 }}>↗</span>
      </a>
    );
  }
  return <span style={{ ...base, color: 'var(--text-secondary)' }}>{formatted}</span>;
}

function SkeletonRows({ count = 5 }) {
  return (
    <div className="loading-container">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton skeleton-row" style={{ opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  );
}

function Podium({ top3 }) {
  const labels = ['1ST', '2ND', '3RD'];
  const classes = ['p1', 'p2', 'p3'];
  const order = [1, 0, 2];
  return (
    <div className="podium-section" style={{ marginBottom: 8 }}>
      {order.map((idx) => {
        const entry = top3[idx];
        if (!entry) return <div key={idx} className={`podium-card ${classes[idx]}`} />;
        return (
          <div key={idx} className={`podium-card ${classes[idx]}`}>
            <div className="podium-rank">{labels[idx]}</div>
            <div className="podium-name">
              <Link to={`/users/${entry.userId}`} style={{ color: 'inherit', textDecoration: 'none' }}>{entry.name}</Link>
            </div>
            <div className="podium-pts">{entry.points} {entry.points === 1 ? 'pt' : 'pts'}</div>
          </div>
        );
      })}
    </div>
  );
}

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [leaderboard, setLeaderboard] = useState([]);
  const [circuitStandings, setCircuitStandings] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [loadingCircuits, setLoadingCircuits] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const lb = await getLeaderboard();
        setLeaderboard(lb);
      } finally {
        setLoadingLeaderboard(false);
      }
    };

    const fetchCircuits = async () => {
      try {
        const circs = await getCircuits();
        const currentYear = new Date().getFullYear();
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allLaps = [];

        await Promise.all(usersSnapshot.docs.map(async (userDoc) => {
          const userLaps = await getUserLaps(userDoc.id, true);
          const currentYearLaps = userLaps.filter(lap => {
            const d = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
            return d.getFullYear() === currentYear;
          });
          allLaps.push(...currentYearLaps.map(lap => ({
            ...lap, userId: userDoc.id, userName: userDoc.data().displayName
          })));
        }));

        const standings = {};
        allLaps.forEach(lap => {
          const circuitId = lap.track.split('-')[0];
          const layoutName = lap.track.split('-').slice(1).join('-');
          if (!standings[circuitId]) standings[circuitId] = [];
          standings[circuitId].push({ ...lap, layoutName });
        });

        const standingsArray = Object.entries(standings).map(([id, laps]) => {
          const circuit = circs.find(c => c.id === id);
          if (!circuit) return null;
          const sorted = laps.sort((a, b) => a.bestLap - b.bestLap);
          const top3 = sorted.slice(0, 3);
          // Get track map for the winning lap's layout
          const winnerLayout = circuit.layouts?.find(l => l.layoutName === top3[0]?.layoutName);
          return { circuit, top3, trackMap: winnerLayout?.map || null };
        }).filter(Boolean);

        setCircuitStandings(standingsArray);
      } finally {
        setLoadingCircuits(false);
      }
    };

    fetchLeaderboard();
    fetchCircuits();
  }, []);

  const top3 = leaderboard.slice(0, 3);

  return (
    <Container maxWidth="xl">
      <div className="page-header">
        <h1 className="page-title">Championship Leaderboard</h1>
        <p className="page-subtitle">2026 Season standings</p>
      </div>

      {!loadingLeaderboard && top3.length > 0 && <Podium top3={top3} />}
      {loadingLeaderboard && <div style={{ marginBottom: 16 }}><SkeletonRows count={3} /></div>}

      {!loadingLeaderboard && leaderboard.length > 0 && (
        <div className="leaderboard-wrap">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th>Driver</th>
                <th style={{ width: 120, textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, i) => (
                <tr key={entry.userId}>
                  <td className="rank-cell">
                    {i < 3 ? (
                      <span className={`rank-badge rank-${i + 1}`}>{i + 1}</span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{i + 1}</span>
                    )}
                  </td>
                  <td>
                    <Link to={`/users/${entry.userId}`} className="user-link">
                      <span className="user-avatar-sm">
                        {entry.headshot ? <img src={entry.headshot} alt={entry.name} /> : (entry.name || '?')[0].toUpperCase()}
                      </span>
                      {entry.name}
                    </Link>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`points-badge ${entry.points === 0 ? 'zero' : ''}`}>{entry.points}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Circuit Standings */}
      <div className="section-gap">
        <h2 className="section-heading">Circuit Standings</h2>
        {loadingCircuits ? (
          <div className="leaderboard-wrap" style={{ padding: 16 }}><SkeletonRows count={4} /></div>
        ) : (
          <div className="leaderboard-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Circuit</th>
                  <th>🥇 1st</th>
                  {!isMobile && <th>🥈 2nd</th>}
                  {!isMobile && <th>🥉 3rd</th>}
                </tr>
              </thead>
              <tbody>
                {circuitStandings.map((standing) => (
                  <tr key={standing.circuit.id}>
                    {/* Circuit name + mini track map */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {standing.trackMap && (
                          <div style={{
                            width: 36, height: 36, flexShrink: 0,
                            background: '#fff', border: '1px solid var(--border)',
                            borderRadius: 6, padding: 3, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            <img src={standing.trackMap} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </div>
                        )}
                        <Link to={`/circuit/${standing.circuit.id}`} className="circuit-name-link">
                          {standing.circuit.name}
                        </Link>
                      </div>
                    </td>
                    <td>
                      {standing.top3[0] ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <Link to={`/users/${standing.top3[0].userId}`} className="user-link" style={{ display: 'inline' }}>
                            {standing.top3[0].userName}
                          </Link>
                          <LapTimeLink time={standing.top3[0].bestLap} proof={standing.top3[0].proof} />
                        </div>
                      ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    {!isMobile && (
                      <td>
                        {standing.top3[1] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Link to={`/users/${standing.top3[1].userId}`} className="user-link" style={{ display: 'inline' }}>
                              {standing.top3[1].userName}
                            </Link>
                            <LapTimeLink time={standing.top3[1].bestLap} proof={standing.top3[1].proof} />
                          </div>
                        ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                    )}
                    {!isMobile && (
                      <td>
                        {standing.top3[2] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Link to={`/users/${standing.top3[2].userId}`} className="user-link" style={{ display: 'inline' }}>
                              {standing.top3[2].userName}
                            </Link>
                            <LapTimeLink time={standing.top3[2].bestLap} proof={standing.top3[2].proof} />
                          </div>
                        ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Container>
  );
}

export default Home;
